import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { createCheckoutSession } from "../lib/api";
import { toast } from "sonner";
import NoIndex from "../components/NoIndex";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await createCheckoutSession(items, origin);
      window.location.href = res.url;
    } catch (e) {
      console.error(e);
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="cart-page">
      <NoIndex title="Your Cart" />
      <h1 className="font-display text-5xl uppercase mb-8 leading-none">
        Your Cart
      </h1>

      {items.length === 0 ? (
        <div className="pw-card p-12 text-center" data-testid="cart-page-empty">
          <ShoppingBag size={48} strokeWidth={3} className="mx-auto mb-4" />
          <h2 className="font-display text-3xl mb-4">Your cart is empty</h2>
          <p className="mb-6 text-lg opacity-70">
            Time to stock up on some Playworld magic.
          </p>
          <Link to="/shop" className="pw-btn">Browse Merch</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {items.map((it) => (
              <div
                key={it.key}
                className="pw-card p-4 flex gap-4"
                data-testid={`cart-page-item-${it.product_id}`}
              >
                <img
                  src={it.image}
                  alt={it.name}
                  className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-lg border-3 border-black"
                  style={{ borderWidth: 3 }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-lg leading-tight">{it.name}</p>
                  {it.size && (
                    <p className="text-sm opacity-70">Size: {it.size}</p>
                  )}
                  <p className="font-display text-xl mt-1">${it.price.toFixed(2)}</p>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <div className="inline-flex items-center border-3 border-black rounded-full overflow-hidden bg-white" style={{ borderWidth: 3 }}>
                      <button
                        className="px-2 py-1 hover:bg-[#FFD500]"
                        onClick={() => updateQty(it.key, it.quantity - 1)}
                        aria-label="Decrease"
                      >
                        <Minus size={14} strokeWidth={3} />
                      </button>
                      <span className="px-3 font-bold">{it.quantity}</span>
                      <button
                        className="px-2 py-1 hover:bg-[#FFD500]"
                        onClick={() => updateQty(it.key, it.quantity + 1)}
                        aria-label="Increase"
                      >
                        <Plus size={14} strokeWidth={3} />
                      </button>
                    </div>
                    <button
                      className="text-[#FF2E93] font-bold inline-flex items-center gap-1 hover:underline"
                      onClick={() => removeItem(it.key)}
                    >
                      <Trash2 size={14} strokeWidth={3} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="pw-card p-6 h-fit sticky top-24">
            <h3 className="font-display text-2xl uppercase mb-4">Summary</h3>
            <div className="flex justify-between mb-2 text-lg">
              <span>Subtotal</span>
              <span className="font-bold" data-testid="cart-page-subtotal">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm opacity-70 mb-4">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              className="pw-btn w-full disabled:opacity-50"
              data-testid="cart-page-checkout-button"
            >
              {loading ? "Redirecting..." : "Proceed to Checkout"}
            </button>
            <p className="text-xs opacity-60 text-center mt-3">
              Secure checkout powered by Stripe
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
